import { BaseEquipment, EquipmentCombinations } from 'fitbeat.models';
import {
  Exercise,
  Level,
  SessionSequence,
  UserExerciseSession,
  WorkoutSettings,
} from '../../../entities';
import { Delays } from '../../../entities/exercise/delays';
import { ISessionSequenceStrategy } from '../isessionsequencestrategy';
import { SequenceContentEntryBuilder } from './sequenceContentEntryBuilder';
interface ISimpleTiming {
  time: number;
}
interface IOldEquipmentTiming {
  bodyweight: number;
  equipment: number;
}
interface IBodyweightLiteTiming {
  bodyweightLite: number;
  equipment: number;
}

interface IDumbbellsLightTiming {
  bodyweightLite: number,
  dumbbellsLight: number,
  equipment: number,
}

function isSimpleTiming(timing: any): timing is ISimpleTiming {
  return timing.time !== undefined;
}

function isOldEquipmentTiming(timing: any): timing is IOldEquipmentTiming {
  return timing.equipment !== undefined && timing.bodyweight !== undefined;
}

function isBodyweightLiteTiming(timing: any): timing is IBodyweightLiteTiming {
  return timing.bodyweightLite !== undefined;
}

function isDumbbellsLiteTiming(timing: any): timing is IDumbbellsLightTiming {
  return timing.dumbbellsLight !== undefined;
}

export interface ISequenceEntryConfig {
  timing: IBodyweightLiteTiming| ISimpleTiming | IOldEquipmentTiming | IDumbbellsLightTiming | {
    [key in EquipmentCombinations]?: number;
  };
  content: SequenceContentEntryBuilder;
}
export abstract class ConfigBasedSequence implements ISessionSequenceStrategy {
  protected abstract configForEachSequenceEntry: () => ISequenceEntryConfig[];
  protected transitionDuration: number = 2;

  public  getVoiceTipsDelaysNew(workDuration: number): Delays {
    const durationForMidTipPoint = 37;
    const midTip = workDuration > durationForMidTipPoint ? Math.floor(workDuration / 2 - 1 ) * 1000 : NaN;
    const lastTip = workDuration * 1000 - 10000;
    return new Delays(150, 3000, midTip, lastTip);
  }

  public getSessionSequence(
    exercise: Exercise,
    alernativeExercise: Exercise,
    secondAlternativeExercise: Exercise,
    thirdAlternateExercise: Exercise,
    zone: number,
    quarter?: number,
    level?: Level,
    workoutSettings?: WorkoutSettings,
    previousExerciseSessions?: UserExerciseSession[],
    exerciseFormatDescription?: string,
  ): SessionSequence[] {
    return this.configForEachSequenceEntry().map(({ timing, content }) => {
      let durationInMilliseconds: number = 0;
      const hasEquipment = workoutSettings && WorkoutSettings.hasEquipment(workoutSettings);
      const previousExercise = hasEquipment
        ? Exercise.getPreviousExerciseWithEquipment(
            BaseEquipment.containsHomeBench,
            previousExerciseSessions,
          )
        : undefined;
      const equipmentCombination = this.getEquipmentCombination(exercise, previousExercise);
      const useDumbbellsLightTiming = isDumbbellsLiteTiming(timing) &&
        equipmentCombination === EquipmentCombinations.DUMBBELLS
      const useSimpleTiming = isSimpleTiming(timing);
      const useOldEquipmentTiming = isOldEquipmentTiming(timing);
      const useBodyWeightLiteTiming = isBodyweightLiteTiming(timing) && !useDumbbellsLightTiming;
      if (useSimpleTiming) {
        durationInMilliseconds = timing.time;
      } else if (useOldEquipmentTiming) {
        durationInMilliseconds = Exercise.isBodyWeightExercise(exercise)
          ? timing.bodyweight
          : timing.equipment;
      } else if (useBodyWeightLiteTiming) {
        durationInMilliseconds = !hasEquipment
          ? timing.bodyweightLite
          : timing.equipment;
      }  else if (useDumbbellsLightTiming) {
        durationInMilliseconds = timing.dumbbellsLight;
      } else if (hasEquipment) {
        durationInMilliseconds = timing[equipmentCombination] as number;
      } else {
        durationInMilliseconds = timing[EquipmentCombinations.BODYWEIGHT_LIGHT] as number;
      }
      return {
        durationInMilliseconds,
        sequence: [{
          ...content.build(
            exercise, alernativeExercise,
            secondAlternativeExercise,
            thirdAlternateExercise,
            zone,
            quarter,
            level,
            workoutSettings,
            exerciseFormatDescription),
          durationInMilliseconds,
        }],
      };
    });
  }
  private getEquipmentCombination(exercise: Exercise, previousExercise?: Exercise) {
    const equipment = exercise.equipment;
    const currentBenchPosition = exercise.homeBenchSetupPosition?.length > 0 ?
      exercise.homeBenchSetupPosition[0] : undefined;
    const previousBenchPosition =  previousExercise && previousExercise.homeBenchSetupPosition?.length > 0 ?
      previousExercise.homeBenchSetupPosition[0] : undefined;
    return BaseEquipment.getEquipmentCombinationForTimingConfigs(equipment, currentBenchPosition,
        previousBenchPosition, exercise.homeBenchSetupAnchorPoint);
  }
}
